/**
 * components/clients/ClientMatchResolverModal.tsx
 *
 * Modal for resolving pending client match requests one by one.
 * Shows the respondent's email, candidate clients with confidence scores,
 * and lets the pro link to an existing client or create a new one.
 * Created Apr 2026.
 * Fix Apr 2026: replaced advance() with a useEffect that resets currentIndex to 0
 *   whenever requests.length changes so no entries are skipped after resolution.
 * Updated Apr 2026: uses getClientDisplayName / getClientSubtitle helpers for
 *   adaptive rendering of both individual and company candidates; passes clientType
 *   to ClientAvatar for differentiated avatar styling.
 */

import { useEffect, useState } from 'react';
import { Mail, UserPlus, Loader2 } from 'lucide-react';
import { Modal } from '../ui/Modal';
import MatchConfidenceBadge from './MatchConfidenceBadge';
import ClientAvatar from '../ui/ClientAvatar';
import Button from '../ui/Button';
import { getClientDisplayName, getClientSubtitle } from '../../utils/clientDisplay';
import type { PendingMatchRequest, ClientCandidate } from '../../types/merge';

interface ClientMatchResolverModalProps {
  isOpen: boolean;
  requests: PendingMatchRequest[];
  onClose: () => void;
  onConfirmMatch: (requestId: string, clientId: string) => Promise<boolean>;
  onCreateNew: (requestId: string) => Promise<boolean>;
  onDismiss: (requestId: string) => void;
  findCandidates: (email: string | null) => Promise<ClientCandidate[]>;
  confirming: boolean;
}

export default function ClientMatchResolverModal({
  isOpen,
  requests,
  onClose,
  onConfirmMatch,
  onCreateNew,
  onDismiss,
  findCandidates,
  confirming,
}: ClientMatchResolverModalProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [candidates, setCandidates] = useState<ClientCandidate[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  const current = requests[currentIndex];

  useEffect(() => {
    if (!isOpen || !current) return;
    setLoadingCandidates(true);
    findCandidates(current.respondent_email).then((c) => {
      setCandidates(c);
      setLoadingCandidates(false);
    });
  }, [isOpen, current?.id, findCandidates]);

  // Reset to index 0 whenever the requests array shrinks (parent removes resolved
  // entry) or the modal re-opens.
  useEffect(() => {
    setCurrentIndex(0);
  }, [requests.length, isOpen]);

  if (!current) return null;

  const handleLink = async (clientId: string) => {
    await onConfirmMatch(current.id, clientId);
  };

  const handleCreateNew = async () => {
    await onCreateNew(current.id);
  };

  const handleSkip = () => {
    onDismiss(current.id);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Rattacher les formulaires" size="lg">
      <div className="flex flex-col gap-5">
        {/* Progress */}
        {requests.length > 1 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-neutral-500">
              {currentIndex + 1} sur {requests.length} formulaires
            </span>
            <div className="flex gap-1">
              {requests.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 w-6 rounded-full transition-colors ${
                    i === currentIndex
                      ? 'bg-primary-600'
                      : i < currentIndex
                      ? 'bg-primary-200'
                      : 'bg-neutral-200'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Request info */}
        <div className="bg-neutral-50 rounded-xl border border-neutral-200 px-4 py-3">
          <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">
            Formulaire complété
          </p>
          <p className="text-sm font-semibold text-neutral-900 truncate">{current.title}</p>
          {current.respondent_email && (
            <div className="flex items-center gap-1.5 mt-1.5 text-sm text-neutral-600">
              <Mail className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
              <span className="truncate">{current.respondent_email}</span>
            </div>
          )}
        </div>

        {/* Candidates */}
        <div>
          <p className="text-sm font-semibold text-neutral-800 mb-2">
            {loadingCandidates
              ? 'Recherche des clients correspondants...'
              : candidates.length > 0
              ? 'Clients correspondants trouvés'
              : 'Aucun client correspondant'}
          </p>

          {loadingCandidates ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 text-neutral-400 animate-spin" />
            </div>
          ) : candidates.length > 0 ? (
            <ul className="space-y-2">
              {candidates.map((c) => {
                const displayName = getClientDisplayName(c);
                const subtitle = getClientSubtitle(c);
                return (
                  <li key={c.id}>
                    <button
                      onClick={() => handleLink(c.id)}
                      disabled={confirming}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-neutral-200 hover:border-primary-300 hover:bg-primary-50 transition-all text-left group disabled:opacity-50"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <ClientAvatar
                          displayName={displayName}
                          clientType={c.client_type}
                          size="sm"
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-neutral-900 truncate">
                            {displayName || 'Client'}
                          </p>
                          {subtitle && (
                            <p className="text-xs text-neutral-500 truncate">{subtitle}</p>
                          )}
                          <div className="mt-1">
                            <MatchConfidenceBadge
                              confidence={c.confidence}
                              reasons={c.matchReasons}
                            />
                          </div>
                        </div>
                      </div>
                      <span className="flex-shrink-0 text-xs font-medium text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity">
                        Rattacher
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-neutral-500 py-3">
              Aucun client existant ne correspond à cet email.
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2 pt-1 border-t border-neutral-100">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleSkip}
            disabled={confirming}
            className="flex-1"
          >
            Passer pour l'instant
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleCreateNew}
            disabled={confirming}
            className="flex-1 flex items-center justify-center gap-1.5"
          >
            <UserPlus className="w-3.5 h-3.5" />
            Créer un nouveau client
          </Button>
        </div>
      </div>
    </Modal>
  );
}
